<template>
  <div class="ma-4">
    <v-toolbar class="mb-4">
      <v-menu v-if="isAdmin || evalAdminOpen" bottom offset-y>
        <template v-slot:activator="{ on }">
          <v-btn text v-on="on">
            Capstone Course Evaluation Overview
            <v-icon right>fal fa-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item to="/student/capstone/admin/evals">
            <v-list-item-title>Capstone Course Evaluations</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isAdmin" to="/student/capstone/admin/students">
            <v-list-item-title>Capstone Course Students</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-toolbar-title v-else>Capstone Course Evaluation Overview</v-toolbar-title>
      <v-spacer />
      <v-menu v-if="terms.length > 1">
        <template v-slot:activator="{ on }">
          <v-btn v-on="on">
            {{ termLabel }}
            <v-icon right>fal fa-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item v-for="{ text, value } in terms" :key="value" @click="term = value">
            <v-list-item-title>{{ text }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <span v-else>{{ termLabel }}</span>
    </v-toolbar>
    <v-card>
      <v-card-text>
        <v-text-field v-model="search" label="Table Search" prepend-inner-icon="fal fa-search" outlined hide-details />
        <v-data-table :items="courses" :headers="headers" :search="search" @dblclick:row="showCourse">
          <template v-slot:item.perc="{ item }">{{ item.perc.toFixed(1) + '%' }}</template>
          <template v-slot:item.view="{ item }">
            <v-btn icon @click="showCourse(null, { item })">
              <v-icon>fal fa-eye</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import { computed, onMounted, ref, watch } from '@vue/composition-api'
import { courseList, deans } from '@/components/student/capstone/courses'

export default {
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const isAdmin = computed(() => user.value.roles.includes('Technology Services') || user.value.roles.includes('Institutional Research') || user.value.roles.includes('Academic Affairs'))
    const isDean = computed(() => {
      const rec = deans.find(({ deans }) => deans ? deans.find(({ pidm }) => pidm === user.value.pidm) : false)
      if (rec) return true
      return false
    })
    const terms = ref([])
    const term = computed({
      get: () => root.$store.state.capstone.term,
      set: (val) => root.$store.commit('capstone/setTerm', val)
    })
    const termLabel = computed(() => {
      const rec = terms.value.find(({ value }) => value === term.value)
      return rec ? rec.text : term.value
    })
    const openDate = ref('')
    const closedDate = ref('')
    const openToDeans = ref(false)
    const openToInstructors = ref(false)
    const evalAdminOpen = computed(() => (isDean.value && openToDeans.value) || openToInstructors.value)
    const courses = ref([])
    const headers = computed(() => {
      const arr = [
        { text: 'Course', value: 'course' },
        { text: 'Instructor', value: 'instructor' },
        { text: 'Student Count', value: 'students' },
        { text: 'Completed', value: 'completed' },
        { text: 'Remaining', value: 'remaining' },
        { text: 'Response Percent', value: 'perc' }
      ]
      if (isAdmin.value || evalAdminOpen.value) {
        arr.push({ text: 'View', value: 'view' })
      }
      return arr
    })

    const search = ref('')

    onMounted(async () => {
      const query = { type: 'Traditional', start: { $lte: new Date() }, capstoneCourseEvals: { $exists: true }, $sort: { start: -1 }, $limit: 10 }
      const { data } = await root.$feathers.service('system/term').find({ query })
      terms.value = data.map(({ label: text, term: value }) => { return { text, value } })
      if (term.value === '') {
        term.value = terms.value[0].value
      }
    })

    watch(term, async () => {
      if (term.value !== '') {
        const { data } = await root.$feathers.service('system/term').find({ query: { term: term.value } })
        if (data.length === 1 && 'capstoneCourseEvals' in data[0]) {
          const { open, close, openToDeans: deanOpenDate, openToInstructors: instructorOpenDate } = data[0].capstoneCourseEvals
          openDate.value = open
          closedDate.value = close
          const now = new Date()
          openToDeans.value = new Date(deanOpenDate) < now
          openToInstructors.value = new Date(instructorOpenDate) < now
          loadData()
        } else {
          alert('There was an error looking up the capstone information for the current term.')
        }
      }
    })

    async function loadData () {
      const aggregate = [
        { $match: { term: term.value } },
        { $addFields: { course: { $substr: ['$title', 0, 6] }, instructor: { $first: '$instructors' } } },
        { $match: {
          course: { $in: courseList }
        } },
        { $unwind: '$students' },
        { $project: { crn: 1, title: 1, section: 1, course: 1, name: '$students.name', pidm: '$students.pidm', instructor: '$instructor.name' } },
        { $lookup: {
          from: 'Student-CapstoneEval',
          localField: 'pidm',
          foreignField: 'pidm',
          as: 'eval'
        } },
        { $group: {
          _id: { crn: '$crn', course: '$course', instructor: '$instructor' },
          students: { $sum: 1 },
          completed: { $sum: { $size: '$eval' } }
        } },
        { $project: {
          crn: '$_id.crn',
          course: '$_id.course',
          instructor: '$_id.instructor',
          students: 1,
          completed: 1,
          remaining: { $subtract: ['$students', '$completed'] },
          perc: { $multiply: [{ $divide: ['$completed', '$students'] }, 100] }
        } },
        { $sort: { course: 1 } }
      ]
      if (isDean.value) {
        // Limit to only the subjects within the dean's area
        const rec = deans.find(({ deans }) => deans ? deans.find(({ pidm }) => pidm === user.value.pidm) : false)
        aggregate[2].$match.course.$in = courseList.filter((course) => rec.subjects.includes(course.substring(0, 3)))
      } else if (!isAdmin.value) {
        // Limit only to their own courses
        aggregate[0].$match['instructors.pidm'] = user.value.pidm
      }
      const { data: courseData } = await root.$feathers.service('calendar/classes').find({ query: { aggregate } })
      courses.value = courseData.map((row) => {
        row.color = row.completed === 0 ? 'error' : (row.completed === row.students ? 'success' : 'warning')
        return row
      })
    }

    function showCourse (e, { item }) {
      if (item.completed > 0) {
        console.log('Setting filter')
        root.$store.commit('capstone/setFilter', { course: [item.course], super: [] })
        root.$router.push('/student/capstone/admin/evals')
      }
    }

    return {
      user,
      isAdmin,
      terms,
      term,
      termLabel,
      openDate,
      closedDate,
      evalAdminOpen,
      courses,
      headers,
      search,
      showCourse
    }
  }
}
</script>
